import React, { useEffect } from 'react';
import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { useAcademy } from '../AcademyContext';
import Breadcrumb from '../components/Breadcrumb';
import VideoPlayer from '../components/VideoPlayer';
import CourseSidebar from '../components/CourseSidebar';
import ProgressBar from '../components/ProgressBar';
import { IconDownload, vaultIcon } from '../components/Icons';

const LessonPage = () => {
  const { topicId, courseId, lessonId } = useParams();
  const navigate = useNavigate();
  const { getTopic, getCourse, getLesson, markWatched, getLessonState, getCourseProgress, getCourseSteps, lessonType } = useAcademy();
  const topic = getTopic(topicId);
  const course = getCourse(topicId, courseId);
  const lesson = getLesson(topicId, courseId, lessonId);

  // Start each lesson at the top of the page (e.g. when arriving from a
  // completed step further down the previous page).
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, [lessonId]);

  if (!topic || !course || !lesson) return <Navigate to="/academy" replace />;

  // This page is for video lessons. Single-type quiz/practice lessons live on
  // their own routes — redirect there if reached directly.
  const type = lessonType(lesson);
  if (type === 'quiz') return <Navigate to={`/academy/${topicId}/${courseId}/${lessonId}/quiz`} replace />;
  if (type === 'practice') return <Navigate to={`/academy/${topicId}/${courseId}/${lessonId}/practice`} replace />;

  const lessonState = getLessonState(topicId, courseId, lessonId);
  const { done, total } = getCourseProgress(topicId, course);

  const handleWatched = () => markWatched(topicId, courseId, lessonId);

  // Determine the next step in the course path (to teaser on the video).
  const MODE_LABEL = { easy: 'Easy Mode', hard: 'Hard Mode', subject: 'Subject Line Mode' };
  const steps = getCourseSteps(topicId, course);
  const curStepIdx = steps.findIndex((s) => s.lesson.id === lessonId);
  let nextStep = null;
  for (let i = curStepIdx + 1; i < steps.length; i += 1) {
    const s = steps[i];
    // Skip exercises that are already done (e.g. when re-watching a finished
    // course) so "next" leads to the next open step, not a completed task.
    if ((s.type === 'quiz' || s.type === 'practice') && s.done) continue;
    nextStep = s;
    break;
  }

  let videoTeaser = null;
  if (nextStep) {
    const s = nextStep;
    if (s.type === 'practice') {
      videoTeaser = {
        kicker: 'Up next · Practice Lab',
        label: MODE_LABEL[s.lesson.practiceLab.mode] || 'Practice',
        to: `/academy/${topicId}/${courseId}/${s.lesson.id}/practice`,
      };
    } else if (s.type === 'quiz') {
      const n = s.lesson.quiz.questions.length;
      videoTeaser = {
        kicker: 'Up next · Mini-Quiz',
        label: `${n} ${n === 1 ? 'question' : 'questions'}`,
        to: `/academy/${topicId}/${courseId}/${s.lesson.id}/quiz`,
      };
    } else {
      videoTeaser = {
        kicker: 'Up next · Video',
        label: s.lesson.title,
        to: `/academy/${topicId}/${courseId}/${s.lesson.id}`,
      };
    }
  }

  return (
    <main className="ac-page ac-page-wide">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Breadcrumb
          items={[
            { label: 'All Topics', to: '/academy' },
            { label: topic.title, to: `/academy/${topic.id}` },
            { label: course.title, to: `/academy/${topic.id}/${course.id}` },
            { label: lesson.title },
          ]}
        />
        <div className="ac-lesson-progress-top">
          <ProgressBar done={done} total={total} />
        </div>
      </div>

      <div className="ac-lesson-layout">
        <div>
          <VideoPlayer
            src={lesson.video?.url}
            poster={lesson.preview}
            posterLabel={lesson.label || lesson.title}
            durationSeconds={lesson.durationSeconds || 60}
            initiallyWatched={!!lessonState.watched}
            onWatched={handleWatched}
            nextStep={videoTeaser}
            onNext={videoTeaser ? () => navigate(videoTeaser.to) : undefined}
          />

          <h1 className="ac-lesson-title">{lesson.title}</h1>
          <p className="ac-lesson-desc">{lesson.description}</p>

          {lesson.cheatsheets && lesson.cheatsheets.length > 0 && (
            <div className="ac-block">
              <p className="ac-block-title">Cheatsheets & Resources</p>
              <div className="ac-cheatsheet-list">
                {lesson.cheatsheets.map((cs) => (
                  <a
                    key={cs.id}
                    href={cs.url || undefined}
                    className="ac-cheatsheet"
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <span className="ac-cheatsheet-icon">{vaultIcon(cs.type)}</span>
                    <span className="ac-cheatsheet-title">{cs.title}</span>
                    <span className="ac-cheatsheet-dl">
                      <IconDownload />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          )}

        </div>

        <CourseSidebar topicId={topicId} course={course} activeLessonId={lessonId} />
      </div>
    </main>
  );
};

export default LessonPage;
